'use client';
import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import { BiSolidStar } from 'react-icons/bi';
import { HiOutlineShieldCheck, HiOutlineTrophy, HiOutlineXCircle } from 'react-icons/hi2';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import LouiseButton from '@/components/buttons/LouiseButton';
import SectionTrustedBy from '@/components/sectionTrustedBy/SectionTrustedBy';

type Props = {
  summery?: string;
  replaceText?: string;
  locale: string;
};

function ComparisonSummary({ summery, replaceText, locale }: Props) {
  const { t } = useTranslation(locale);

  const getSummary = (text?: string) => {
    const str = text?.replace('LouiseBot', '<strong class="tw-text-blue-600 tw-font-bold">LouiseBot</strong>');
    if (replaceText) {
      return str?.replace(replaceText, `<strong class="tw-text-purple-600 tw-font-bold">${replaceText}</strong>`);
    }
    return str;
  };

  const benefits = [
    {
      icon: AiOutlineThunderbolt,
      text: t('ComparisonSummary/benefit-1'),
      highlight: true,
    },
    {
      icon: HiOutlineShieldCheck,
      text: t('ComparisonSummary/benefit-2'),
      highlight: false,
    },
    {
      icon: HiOutlineTrophy,
      text: t('ComparisonSummary/benefit-3'),
      highlight: false,
    },
  ];

  const stats = [
    { value: t('ComparisonSummary/only'), label: t('ComparisonSummary/metric1') },
    { value: '1.200+', label: t('ComparisonSummary/metric2') },
    { value: '5 min', label: t('ComparisonSummary/metric3') },
  ];

  return (
    <section className="tw-py-20 tw-font-['Lexend']">
      <div className="tw-relative tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Header */}
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <HiOutlineTrophy className="tw-w-4 tw-h-4" />
            {t('ComparisonSummary/badge')}
          </div>
          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
            {t('ComparisonSummary/title')}
          </h2>
          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-leading-relaxed">
            {t('ComparisonSummary/subtitle')}
          </p>
        </div>

        {/* Main Content Card */}
        <div className="tw-relative tw-bg-gradient-to-br tw-from-white tw-to-gray-50 tw-rounded-3xl tw-shadow-2xl tw-border tw-border-gray-200 tw-overflow-hidden">
          {/* Background Decorations */}
          <div className="tw-absolute tw-top-0 tw-right-0 tw-w-96 tw-h-96 tw-bg-gradient-to-l tw-from-blue-100/30 tw-to-transparent tw-rounded-full tw-blur-3xl"></div>
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-80 tw-h-80 tw-bg-gradient-to-r tw-from-purple-100/30 tw-to-transparent tw-rounded-full tw-blur-3xl"></div>

          <div className="tw-relative tw-z-10 tw-grid lg:tw-grid-cols-2 tw-gap-12 tw-p-8 lg:tw-p-16">
            {/* Content Side */}
            <div className="tw-space-y-8">
              {/* Introduction */}
              <div className="tw-space-y-6">
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                  <div className="tw-w-12 tw-h-12 tw-bg-blue-600 tw-rounded-2xl tw-flex tw-items-center tw-justify-center">
                    <HiOutlineXCircle className="tw-w-6 tw-h-6 tw-text-white" />
                  </div>
                  <span className="tw-text-blue-600 tw-font-semibold tw-text-lg">{t('ComparisonSummary/l-01')}</span>
                </div>

                <div
                  className="tw-text-gray-700 tw-text-lg tw-leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: getSummary(summery) as string }}
                />
              </div>

              {/* Benefits */}
              <div className="tw-space-y-4">
                <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-6">
                  {t('ComparisonSummary/why-choose-title')}
                </h3>
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className={`tw-flex tw-items-center tw-gap-4 tw-p-4 tw-rounded-xl tw-transition-all tw-duration-300 ${
                      benefit.highlight
                        ? 'tw-bg-blue-50 tw-border tw-border-blue-200'
                        : 'tw-bg-gray-50 tw-border tw-border-gray-200'
                    } hover:tw-shadow-md`}
                  >
                    <div
                      className={`tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center ${
                        benefit.highlight ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-600 tw-text-white'
                      }`}
                    >
                      <benefit.icon className="tw-w-5 tw-h-5" />
                    </div>
                    <span className="tw-text-gray-700 tw-font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>

              {/* Stats */}
              <div className="tw-grid grid-cols-3 tw-gap-6 tw-py-8 tw-border-t tw-border-b tw-border-gray-200">
                {stats.map((stat, index) => (
                  <div key={index} className="tw-text-center">
                    <div className="tw-flex tw-items-center tw-justify-center tw-mb-2">
                      <span className="tw-text-2xl tw-font-bold tw-text-gray-900">{stat.value}</span>
                    </div>
                    <div className="tw-text-sm tw-text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Final CTA */}
              <div className="tw-space-y-6">
                <div
                  className="tw-text-gray-700 tw-text-base tw-leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: getSummary(t('ComparisonSummary/l-09')) as string }}
                />

                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4">
                  <LouiseButton
                    color="blue"
                    cta={t('SiteNavbar/l-04')}
                    ctaTarget="register"
                    icon={<AiOutlineThunderbolt className="tw-w-5 tw-h-5" />}
                    gradiant={true}
                    locale={locale}
                  />
                </div>
              </div>
            </div>

            {/* Image Side */}
            <div className="tw-relative tw-flex tw-items-center tw-justify-center">
              {/* Main Image */}
              <div className="tw-relative tw-group">
                <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-blue-400/20 tw-to-purple-400/20 tw-rounded-3xl tw-blur-2xl tw-scale-110 tw-transition-transform tw-duration-500 group-hover:tw-scale-125"></div>

                <div className="tw-relative tw-bg-white tw-p-8 tw-rounded-3xl tw-shadow-xl tw-border tw-border-gray-200 tw-transition-transform tw-duration-500 group-hover:tw-scale-105">
                  <Image
                    src="https://cdn.1cdn.app/application/LOUISEBOT/24081711380377_Hesitation.svg"
                    alt={t('ComparisonSummary/image-alt')}
                    width={400}
                    height={400}
                    className="tw-w-full tw-h-auto tw-max-w-sm tw-mx-auto"
                    priority
                  />
                </div>
              </div>

              {/* Floating Elements */}
              <div className="tw-absolute tw-top-8 tw--left-4 tw-bg-white tw-rounded-2xl tw-p-4 tw-shadow-lg tw-border tw-border-gray-200 tw-transform tw-rotate-[-5deg]">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <HiOutlineXCircle className="tw-w-6 tw-h-6 tw-text-green-500" />
                  <div>
                    <div className="tw-text-sm tw-font-bold tw-text-gray-900">✓ {t('ComparisonSummary/feature-1')}</div>
                  </div>
                </div>
              </div>

              <div className="tw-absolute tw-bottom-8 tw--right-4 tw-bg-white tw-rounded-2xl tw-p-4 tw-shadow-lg tw-border tw-border-gray-200 tw-transform tw-rotate-[5deg]">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <div className="tw-flex">
                    {[...Array(5)].map((_, i) => (
                      <BiSolidStar key={i} className="tw-w-4 tw-h-4 tw-text-yellow-400" />
                    ))}
                  </div>
                  <div className="tw-text-sm tw-font-bold tw-text-gray-900">4.8/5</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Banner */}
          <div className="tw-relative tw-z-10 tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-p-6 tw-text-center">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-6 tw-text-white">
              <div className="tw-flex tw-items-center tw-gap-2">
                <HiOutlineXCircle className="tw-w-5 tw-h-5" />
                <span className="tw-font-medium">{t('ComparisonSummary/guarantee-1')}</span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <HiOutlineShieldCheck className="tw-w-5 tw-h-5" />
                <span className="tw-font-medium">{t('ComparisonSummary/guarantee-2')}</span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                <AiOutlineThunderbolt className="tw-w-5 tw-h-5" />
                <span className="tw-font-medium">{t('ComparisonSummary/guarantee-3')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ComparisonSummary;
